import React from "react";
import * as L from "leaflet";
import { HandleInputFile } from "./handleInputFile";

interface MenuProps {
	map: L.Map;
	toggleFromMap: (feature: L.FeatureGroup) => void;
	addMarkers?: (points: L.LatLng[]) => L.FeatureGroup;
	addPolyline?: (points: L.LatLng[]) => L.FeatureGroup;
	addPolygon?: (points: L.LatLng[]) => L.FeatureGroup;
	addOverlay?: (sw: L.LatLngExpression, ne: L.LatLngExpression, file: File) => L.FeatureGroup;
	startDrawingRoute?: () => void;
	finishDrawingRoute?: () => L.FeatureGroup | null;
	cancelDrawingRoute?: () => void;
	isDrawingRoute?: boolean;
	routePoints?: L.LatLng[];
}

interface MenuItem {
	id: string;
	label: string;
	icon: JSX.Element;
	fileInputId: string;
	handler?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onClick?: (e: React.MouseEvent) => void;
	isActive?: boolean;
	badge?: number;
}

export const Menu = ({
	map,
	toggleFromMap,
	addMarkers,
	addPolyline,
	addPolygon,
	addOverlay,
	startDrawingRoute,
	finishDrawingRoute,
	cancelDrawingRoute,
	isDrawingRoute = false,
	routePoints = []
}: MenuProps) => {

	const handleRouteClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (!isDrawingRoute) {
			startDrawingRoute?.();
		} else if (routePoints.length >= 2) {
			finishDrawingRoute?.();
		}
	};

	const handleRouteCancelClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		cancelDrawingRoute?.();
	};

	const menuItems: MenuItem[] = [
		{
			id: "marker",
			label: "Marker",
			icon: <MarkerIcon />,
			fileInputId: "fileInputMarker",
			handler: (event) => map && HandleInputFile({ event, map, toggleFromMap, addMarkers })
		},
		{
			id: "line",
			label: "Line",
			icon: <LineIcon />,
			fileInputId: "fileInputPolyline",
			handler: (event) => map && HandleInputFile({ event, map, toggleFromMap, addPolyline })
		},
		{
			id: "polygon",
			label: "Polygon",
			icon: <PolygonIcon />,
			fileInputId: "fileInputPolygon",
			handler: (event) => map && HandleInputFile({ event, map, toggleFromMap, addPolygon })
		},
		{
			id: "overlay",
			label: "Overlay",
			icon: <OverlayIcon />,
			fileInputId: "fileInputOverlay",
			handler: (event) => map && HandleInputFile({ event, map, toggleFromMap, addOverlay })
		},
		{
			id: "route",
			label: getRouteTooltip(isDrawingRoute, routePoints.length),
			icon: <RouteIcon />,
			fileInputId: "",
			onClick: handleRouteClick,
			isActive: isDrawingRoute,
			badge: isDrawingRoute ? routePoints.length : undefined
		}
	];

	return (
		<nav className="menu">
			<ul className="menuitem">
				{menuItems.map((item) => (
					<MenuItemComponent
						key={item.id}
						item={item}
						showCancelButton={item.id === "route" && isDrawingRoute}
						onCancel={handleRouteCancelClick}
					/>
				))}
			</ul>
		</nav>
	);
};

// Componente de item de menu
interface MenuItemComponentProps {
	item: MenuItem;
	showCancelButton: boolean;
	onCancel: (e: React.MouseEvent) => void;
}

const MenuItemComponent = ({ item, showCancelButton, onCancel }: MenuItemComponentProps) => {
	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();

		if (item.onClick) {
			item.onClick(e);
		} else if (item.handler) {
			const fileInput = document.getElementById(item.fileInputId) as HTMLInputElement;
			fileInput?.click();
		}
	}

	return (
		<li className={`icon-content ${item.isActive ? 'active-route' : ''}`}>
			{item.handler && (
				<input
					id={item.fileInputId}
					type="file"
					className="file-input"
					onChange={item.handler}
				/>
			)}

			<a
				href="#"
				aria-label={item.label}
				data-social={item.id}
				onClick={handleClick}
				className="menu-link"
			>
				<div className="filled"></div>
				{item.icon}

				{item.badge && item.badge > 0 && (
					<RouteBadge count={item.badge} />
				)}
			</a>

			<div className="tooltip">{item.label}</div>

			{showCancelButton && (
				<CancelButton onClick={onCancel} />
			)}
		</li>
	)
}

const MarkerIcon = () => (
	<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
		<g id="Layer_88" data-name="Layer 88">
			<path d="M32,2.5c-10.89,0-19.75,9.35-19.75,20.85,0,11,17.77,36.44,18.52,37.51a1.51,1.51,0,0,0,2.46,0C34,59.79,51.75,34.39,51.75,23.35,51.75,11.85,42.89,2.5,32,2.5Zm0,54.85c-4.05-6-16.75-25.47-16.75-34C15.25,13.51,22.76,5.5,32,5.5s16.75,8,16.75,17.85C48.75,31.88,36.05,51.35,32,57.35Z"></path>
			<path d="M32,11.13A12.24,12.24,0,0,0,19.77,23.35c.68,16.22,23.79,16.22,24.46,0A12.24,12.24,0,0,0,32,11.13Zm0,21.45a9.24,9.24,0,0,1-9.23-9.23c.5-12.23,18-12.23,18.46,0A9.24,9.24,0,0,1,32,32.58Z"></path>
		</g>
	</svg>
)

const LineIcon = () => (
	<svg fill="currentColor" version="1.0" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="64px" height="64px" viewBox="0 0 64 64" enableBackground="new 0 0 64 64">
		<rect x="1" y="53" fill="currentColor" stroke="#000000" strokeWidth="2" strokeMiterlimit="10" width="10" height="10"></rect>
		<rect x="53" y="1" fill="currentColor" stroke="#000000" strokeWidth="2" strokeMiterlimit="10" width="10" height="10"></rect>
		<line fill="currentColor" stroke="#000000" strokeWidth="2" strokeMiterlimit="10" x1="11" y1="53" x2="53" y2="11"></line>
	</svg>
)

const PolygonIcon = () => (
	<svg width="800px" height="800px" viewBox="0 0 48 48" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
		<path fillRule="evenodd" clipRule="evenodd" d="M14 40C15.1046 40 16 39.1046 16 38C16 36.8954 15.1046 36 14 36C12.8954 36 12 36.8954 12 38C12 39.1046 12.8954 40 14 40ZM14 42C16.2091 42 18 40.2091 18 38C18 35.7909 16.2091 34 14 34C11.7909 34 10 35.7909 10 38C10 40.2091 11.7909 42 14 42Z" fill="currentColor" />
		<path fillRule="evenodd" clipRule="evenodd" d="M10 22C11.1046 22 12 21.1046 12 20C12 18.8954 11.1046 18 10 18C8.89543 18 8 18.8954 8 20C8 21.1046 8.89543 22 10 22ZM10 24C12.2091 24 14 22.2091 14 20C14 17.7909 12.2091 16 10 16C7.79086 16 6 17.7909 6 20C6 22.2091 7.79086 24 10 24Z" fill="currentColor" />
		<path fillRule="evenodd" clipRule="evenodd" d="M38 22C39.1046 22 40 21.1046 40 20C40 18.8954 39.1046 18 38 18C36.8954 18 36 18.8954 36 20C36 21.1046 36.8954 22 38 22ZM38 24C40.2091 24 42 22.2091 42 20C42 17.7909 40.2091 16 38 16C35.7909 16 34 17.7909 34 20C34 22.2091 35.7909 24 38 24Z" fill="currentColor" />
		<path fillRule="evenodd" clipRule="evenodd" d="M34 40C35.1046 40 36 39.1046 36 38C36 36.8954 35.1046 36 34 36C32.8954 36 32 36.8954 32 38C32 39.1046 32.8954 40 34 40ZM34 42C36.2091 42 38 40.2091 38 38C38 35.7909 36.2091 34 34 34C31.7909 34 30 35.7909 30 38C30 40.2091 31.7909 42 34 42Z" fill="currentColor" />
		<path fillRule="evenodd" clipRule="evenodd" d="M24 12C25.1046 12 26 11.1046 26 10C26 8.89543 25.1046 8 24 8C22.8954 8 22 8.89543 22 10C22 11.1046 22.8954 12 24 12ZM24 14C26.2091 14 28 12.2091 28 10C28 7.79086 26.2091 6 24 6C21.7909 6 20 7.79086 20 10C20 12.2091 21.7909 14 24 14Z" fill="currentColor" />
		<path fillRule="evenodd" clipRule="evenodd" d="M34.9188 19.028L25.9188 12.5995L27.0812 10.972L36.0812 17.4006L34.9188 19.028ZM21.7844 12.8115L13.0812 19.028L11.9188 17.4006L20.622 11.184L21.7844 12.8115ZM11.6429 22.7831L14.3095 34.7831L12.3572 35.2169L9.69049 23.2169L11.6429 22.7831ZM33.6905 34.7831L36.246 23.2831L38.1984 23.7169L35.6429 35.2169L33.6905 34.7831ZM17 37H31V39H17V37Z" fill="currentColor" />
	</svg>
)

const OverlayIcon = () => (
	<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
		<rect x="10" y="10" width="44" height="44" stroke="black" fill="currentColor" />
	</svg>
)

const RouteIcon = () => (
	<svg
		fill="currentColor"
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		width="64"
		height="64"
	>
		<path d="M19 15l-6 6-1.42-1.42L15.17 16H4V4h2v10h9.17l-3.59-3.58L13 9l6 6z" />
	</svg>
)

const RouteBadge = ({ count }: { count: number }) => (
	<span className="route-badge">
		{count}
	</span>
)

const CancelButton = ({ onClick }: { onClick: (e: React.MouseEvent) => void }) => (
	<button
		onClick={onClick}
		className="cancel-button"
		title="Cancel route"
		type="button"
	>
		✕
	</button>
)

const getRouteTooltip = (isDrawing: boolean, pointsCount: number): string => {
	if (!isDrawing) return 'Draw Route';
	return pointsCount >= 2 ? 'Click to Finish' : `Points: ${pointsCount}`;
}